/**
 * O atendimento aberto (spec 018, US3): conversa e ação no centro.
 *
 * Medido em `5c943de`: 8 abas, ~20 cartões, ~40 controles fixos e ~15
 * diálogos — e a conversa do WhatsApp numa aba separada dos comentários. Aqui:
 * cabeçalho (situação, responsável editável, prazo, Concluir, Detalhes, ⋯), a
 * solicitação inicial, UMA linha do tempo e o compositor com modos. O resto
 * (solicitante, cliente e recurso, mesa, prioridade, status, etapa, prazos,
 * campos adicionais, valores, origem, seguidores) fica em Detalhes, um painel
 * sobreposto fechado por padrão (FR-005). "Registrar ação" junta tempo,
 * compromisso, OS e checklist — os mesmos componentes do Completo.
 *
 * Nenhuma escrita nova: tudo passa pelos endpoints de sempre, com as guardas
 * de sempre (espelhado da 011, permissões, pausa da 015 — FR-006).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useRouter, useCanGoBack, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ArrowLeft,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Ellipsis,
  ExternalLink,
  PanelRightOpen,
  RotateCcw,
  Sparkles,
  Square,
  Timer,
  Users,
  Wrench,
  XCircle,
} from 'lucide-react';
import {
  ORIGIN_LABELS,
  capitalize,
  type ChatListItem,
  type EntityValue,
  type TicketDetail,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog, Spinner } from '@/components/ui/misc';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { api } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useTerms } from '@/shared/tenant-profile';
import { chatsListOptions } from '@/features/chats/chats-queries';
import { ticketCloseOptionsQuery, ticketDetailQuery } from '@/features/tickets/tickets-queries';
import { CloseTicketDialog } from '@/features/tickets/close-ticket-dialog';
import { TeamMemberPicker } from '@/features/team/TeamMemberPicker';
import { usePatchTicket, useStopTimer, useTicketAction } from '@/features/tickets/tickets-mutations';
import { errorMessage, isTicketMirrored, isTicketReadOnly } from '@/features/tickets/ticket-format';
import { RequestorCard, TicketInfoCard } from '@/features/tickets/detail-info-tab';
import { TechnicianPicker } from '@/features/service-orders/TechnicianPicker';
import { SlaCard } from '@/features/tickets/detail-sla-card';
import { TicketEntitiesCard } from '@/features/tickets/detail-entities';
import { AttachmentsSection } from '@/features/tickets/detail-attachments';
import { TicketRatingCard } from '@/features/tickets/detail-rating';
import { ValoresTab } from '@/features/tickets/detail-valores-tab';
import { ApontamentosTab } from '@/features/tickets/detail-apontamentos-tab';
import { ChecklistsTab } from '@/features/tickets/detail-checklists';
import { ChangeClientDialog } from '@/features/tickets/ChangeClientDialog';
import { MirroredBadge } from '@/features/tickets/mirrored-badge';
import { AiSummaryDialog, useAiActive } from '@/features/tickets/ai-actions';
import { TicketDispatchBlock, useTicketDispatchMenu } from '@/features/tickets/ticket-dispatch-block';
import { StartWhatsappAction } from '@/features/tickets/start-whatsapp-action';
import { ServiceOrdersTab } from '@/features/service-orders/detail-service-orders-tab';
import { CommitmentDrawer } from '@/features/agenda/commitment-drawer';
import { PlateCommitmentDrawer } from '@/features/agenda/plate-commitment-drawer';
import { useAssumeWork } from '@/features/work/work-actions';
import { TONE_TEXT, dueInfo } from '@/features/work/work-format';
import { WORK_KEY } from '@/features/work/work-queries';
import { SituationChip } from './situation-chip';
import { RequestCard } from './request-card';
import { TIMELINE_KEY, Timeline, useTicketTimeline } from './timeline';
import { Composer, type NoteTarget, type ReplyChannel } from './composer';
import { useDrafts } from './drafts';
import { lastList } from './atendimentos-search';

export const atendimentoSearchSchema = z.object({
  /** "Responder" do Hoje: compositor em foco no canal certo */
  responder: z.coerce.number().optional().catch(undefined),
  /** painel de Detalhes aberto (compartilhável) */
  detalhes: z.coerce.number().optional().catch(undefined),
});
export type AtendimentoSearch = z.infer<typeof atendimentoSearchSchema>;

type ActionSheet = 'tempo' | 'os' | 'checklist' | 'compromisso' | 'agendar' | null;

export function AtendimentoPage() {
  const params = useParams({ strict: false }) as { ticketNumber?: string };
  const ticketNumber = Number(params.ticketNumber ?? 0);
  const search = atendimentoSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const qc = useQueryClient();
  const { can, ready } = usePermissions();
  const terms = useTerms();

  const detailQ = useQuery({
    ...ticketDetailQuery(ticketNumber),
    // cronômetro correndo: o tempo e o indicador acompanham
    refetchInterval: (q) => ((q.state.data?.runningTimerUserIds.length ?? 0) > 0 ? 30_000 : false),
  });
  const canChats = ready && can('chat.access');
  const chatsQ = useQuery({ ...chatsListOptions({ tab: 'all', ticketNumber }), enabled: canChats });
  const timeline = useTicketTimeline(ticketNumber);
  const drafts = useDrafts(`erp.draft.ticket.${ticketNumber}`);

  const back = () => {
    // veio da lista (ou do Hoje): volta NO HISTÓRICO — visão, filtros, página e
    // rolagem como estavam (US2 AS2); link direto: a última lista desta aba
    if (canGoBack) router.history.back();
    else void navigate({ to: '/s/atendimentos', search: lastList() });
  };

  if (detailQ.isPending) {
    return (
      <div className="mx-auto max-w-4xl space-y-3">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detailQ.isError) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center gap-3 rounded-lg border bg-card p-10 text-center shadow-sm">
        <p className="font-semibold">{capitalize(terms.ticket)} não encontrado</p>
        <p className="text-sm text-muted-foreground">
          O #{ticketNumber} não existe ou você não tem acesso a ele.
        </p>
        <Button variant="outline" size="sm" onClick={back}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <TicketView
      ticket={detailQ.data}
      linkedChats={chatsQ.data ?? []}
      // o compositor escolhe o canal pelas conversas e pela linha do tempo:
      // só nasce depois delas, para não trocar de canal debaixo de quem digita
      composerReady={!(canChats && chatsQ.isPending) && !timeline.isPending}
      timeline={timeline}
      drafts={drafts}
      search={search}
      onBack={back}
      onSent={() =>
        // resolve quando a linha do tempo já trouxe o que acabou de ser escrito
        Promise.all([
          qc.invalidateQueries({ queryKey: [...TIMELINE_KEY, { ticketNumber }] }),
          qc.invalidateQueries({ queryKey: ticketDetailQuery(ticketNumber).queryKey }),
          qc.invalidateQueries({ queryKey: WORK_KEY }),
        ]).then(() => undefined)
      }
      setDetails={(open) =>
        void navigate({
          to: '/s/atendimentos/$ticketNumber',
          params: { ticketNumber: String(ticketNumber) },
          search: { ...search, detalhes: open ? 1 : undefined },
          replace: true,
        })
      }
    />
  );
}

function TicketView({
  ticket,
  linkedChats,
  composerReady,
  timeline,
  drafts,
  search,
  onBack,
  onSent,
  setDetails,
}: {
  ticket: TicketDetail;
  linkedChats: ChatListItem[];
  composerReady: boolean;
  timeline: ReturnType<typeof useTicketTimeline>;
  drafts: ReturnType<typeof useDrafts>;
  search: AtendimentoSearch;
  onBack: () => void;
  onSent: () => Promise<void>;
  setDetails: (open: boolean) => void;
}) {
  const me = useAuthUser();
  const terms = useTerms();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, ready } = usePermissions();
  const mirrored = isTicketMirrored(ticket);
  const readOnly = isTicketReadOnly(ticket);
  const closed = ticket.status.kind === 'closed' || ticket.status.kind === 'canceled';
  const action = useTicketAction(ticket.ticketNumber);
  const stopTimer = useStopTimer(ticket.ticketNumber);
  const aiActive = useAiActive();
  // spec 014/025: "Disparar agora" / "Tirar da fila" — a mesma regra, mutação e
  // confirmação da visão completa
  const dispatchMenu = useTicketDispatchMenu(ticket);
  const [sheet, setSheet] = useState<ActionSheet>(null);
  const [confirm, setConfirm] = useState<'cancel' | 'reopen' | null>(null);
  const [concluding, setConcluding] = useState<{ draft: boolean; missing: string[] } | null>(null);
  // spec 054: a mesa com motivo de fechamento pede o motivo antes de concluir
  const [closeDialog, setCloseDialog] = useState(false);
  const [changeClient, setChangeClient] = useState(false);
  // spec 057: a conversa aberta daqui vira o canal da resposta
  const [startedChatId, setStartedChatId] = useState<number | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const now = new Date();
  const due = dueInfo(ticket, now);

  // ── canais ───────────────────────────────────────────────────────────────
  const openChats = linkedChats.filter((c) => c.status !== 'archived');
  const channels = useMemo(() => {
    const list: Array<{ channel: ReplyChannel; label: string }> = openChats.map((c) => ({
      channel: { kind: 'chat', chatId: c.id },
      label: `${c.origin === 'whatsapp' ? 'WhatsApp' : c.origin === 'widget' ? 'Chat do site' : 'Conversa'} · ${c.requestorName}`,
    }));
    // espelhado (011): comentário é do TiFlux; o WhatsApp é do ERP e segue (AS1)
    if (!mirrored) {
      list.push({
        channel: { kind: 'comment' },
        label: ticket.requestorEmail ? 'Comentário público / e-mail' : 'Comentário público',
      });
    }
    return list;
  }, [openChats, mirrored, ticket.requestorEmail]);

  // o canal certo: onde o cliente falou por último; senão o WhatsApp vinculado
  const defaultChannel = useMemo<ReplyChannel | null>(() => {
    // spec 057: a conversa que acabou de ser aberta daqui é o canal
    if (startedChatId !== null && openChats.some((c) => c.id === startedChatId)) {
      return { kind: 'chat', chatId: startedChatId };
    }
    const lastClient = [...timeline.entries]
      .reverse()
      .find(
        (e) => (e.kind === 'chat_message' && e.direction === 'inbound') || (e.kind === 'comment' && e.authorKind === 'client')
      );
    if (lastClient?.kind === 'chat_message' && openChats.some((c) => c.id === lastClient.chatId)) {
      return { kind: 'chat', chatId: lastClient.chatId };
    }
    if (lastClient?.kind === 'comment' && !mirrored) return { kind: 'comment' };
    if (openChats[0]) return { kind: 'chat', chatId: openChats[0].id };
    return mirrored ? null : { kind: 'comment' };
  }, [timeline.entries, openChats, mirrored, startedChatId]);

  const noteTarget: NoteTarget = mirrored
    ? openChats[0]
      ? { kind: 'chat', chatId: openChats[0].id }
      : { kind: 'none', reason: 'Ticket espelhado do TiFlux — a nota vai pelo TiFlux (não há conversa vinculada aqui).' }
    : ready && !can('internal_comm.view_add')
      ? { kind: 'none', reason: 'Sem permissão para notas internas.' }
      : { kind: 'ticket', ticketNumber: ticket.ticketNumber };

  // ── Concluir (US3 AS3): avisa antes se há texto não enviado ou campo obrigatório vazio
  const concluir = async () => {
    setActionError(null);
    let missing: string[] = [];
    try {
      const values = await qc.fetchQuery({
        queryKey: ['erp', 'tickets', 'entities', { ticketNumber: ticket.ticketNumber }],
        queryFn: async () => {
          const res = await api<EntityValue[]>(`/tickets/${ticket.ticketNumber}/entities`);
          if (res.error) throw res.error;
          return res.data;
        },
      });
      missing = values
        .filter((v) => v.required && (v.value === null || v.value.trim() === '') && v.optionId === null)
        .map((v) => v.fieldName);
    } catch {
      // sem conseguir ler os campos, não se bloqueia nada — só não se avisa deles
    }
    if (drafts.pending || missing.length > 0) {
      setConcluding({ draft: drafts.pending, missing });
      return;
    }
    await proceedToClose();
  };
  // spec 054: na mesa com motivo ativo, o fechamento vai pelo diálogo do motivo;
  // na sem motivo, fecha direto, como sempre
  const proceedToClose = async () => {
    let required = true;
    try {
      required = (await qc.fetchQuery(ticketCloseOptionsQuery(ticket.ticketNumber))).required;
    } catch {
      // sem saber, pergunta: o diálogo mostra o erro, e quem decide é a API
    }
    if (required) {
      setConcluding(null);
      setCloseDialog(true);
    } else close();
  };
  const close = () =>
    action.mutate('close', {
      onSuccess: () => {
        setConcluding(null);
        void onSent();
      },
      onError: (e) => setActionError(errorMessage(e)),
    });

  const runningMine = me !== null && ticket.runningTimerUserIds.includes(me.id);

  // o mais recente fica logo acima do compositor (fixo embaixo): quem acabou de
  // enviar vê o que enviou, e quem chega pelo "Responder" vê o que o cliente disse
  const endRef = useRef<HTMLDivElement>(null);
  const revealLatest = () => requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: 'end' }));
  const sentFromComposer = () => void onSent().then(revealLatest);
  const revealed = useRef(false);
  useEffect(() => {
    if (search.responder !== 1 || revealed.current || !composerReady || timeline.isPending) return;
    revealed.current = true;
    revealLatest();
  }, [search.responder, composerReady, timeline.isPending]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-3 pb-4">
      {/* ── cabeçalho ───────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-10 -mx-4 -mt-4 border-b bg-background/95 px-4 pt-3 pb-3 backdrop-blur">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label="Voltar" className="mt-0.5 shrink-0">
            <ArrowLeft />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex min-w-0 items-center gap-2 text-lg leading-tight font-semibold">
              <span className="shrink-0 text-muted-foreground tabular-nums">#{ticket.ticketNumber}</span>
              <span className="truncate" title={ticket.title}>
                {ticket.title}
              </span>
              {mirrored ? <MirroredBadge /> : null}
            </h1>
            <p className="truncate text-xs text-muted-foreground">
              {[ticket.client.name, ticket.resourceLabel, ticket.desk.name, ticket.stage.name].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {mirrored ? null : closed ? (
              <Button variant="outline" size="sm" onClick={() => setConfirm('reopen')}>
                <RotateCcw />
                Reabrir
              </Button>
            ) : (
              <Button size="sm" onClick={() => void concluir()} disabled={action.isPending}>
                {action.isPending ? <Spinner className="size-4 border-primary-foreground" /> : <CheckCircle2 />}
                Concluir
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setDetails(true)}>
              <PanelRightOpen />
              Detalhes
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" aria-label="Mais ações">
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                {!mirrored ? (
                  <DropdownMenuItem onSelect={() => setChangeClient(true)}>
                    <Users />
                    Trocar {terms.client}
                  </DropdownMenuItem>
                ) : null}
                {aiActive ? (
                  <DropdownMenuItem onSelect={() => setAiOpen(true)}>
                    <Sparkles />
                    Resumir com IA
                  </DropdownMenuItem>
                ) : null}
                {dispatchMenu.items}
                <DropdownMenuItem
                  onSelect={() =>
                    void navigate({
                      to: '/tickets/$ticketNumber',
                      params: { ticketNumber: String(ticket.ticketNumber) },
                      search: { completo: 1 } as never,
                    })
                  }
                >
                  <ExternalLink />
                  Abrir na visão completa
                </DropdownMenuItem>
                {!mirrored && !closed ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setConfirm('cancel')}>
                      <XCircle />
                      Cancelar {terms.ticket}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-9 text-xs">
          <SituationChip situation={ticket.situation} stuck={ticket.stuckInStage} />
          <ResponsibleControl ticket={ticket} readOnly={readOnly} />
          <TechnicianControl ticket={ticket} />
          <span className={cn('font-medium', TONE_TEXT[due.tone])} title="Prazo">
            {due.text}
          </span>
          {ticket.runningTimerUserIds.length > 0 ? (
            // cronômetro ativo: indicador PERSISTENTE no cabeçalho (US3 AS4)
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-medium text-primary">
              <Timer className="size-3.5 animate-pulse" />
              {runningMine ? 'Seu cronômetro está correndo' : 'Cronômetro em andamento'}
              {runningMine ? (
                <button
                  type="button"
                  className="ml-1 inline-flex items-center gap-0.5 rounded px-1 hover:bg-primary/15"
                  disabled={stopTimer.isPending}
                  onClick={() => stopTimer.mutate(null, { onSuccess: onSent, onError: (e) => setActionError(errorMessage(e)) })}
                >
                  <Square className="size-3" />
                  Parar
                </button>
              ) : null}
            </span>
          ) : null}
        </div>
        {actionError ? <p className="mt-1 pl-9 text-xs text-destructive">{actionError}</p> : null}
      </header>

      <RequestCard ticket={ticket} />

      <TicketDispatchBlock ticketNumber={ticket.ticketNumber} />

      <Timeline
        ticketNumber={ticket.ticketNumber}
        entries={timeline.entries}
        isPending={timeline.isPending}
        isError={timeline.isError}
        hasOlder={timeline.hasNextPage}
        loadingOlder={timeline.isFetchingNextPage}
        onLoadOlder={() => void timeline.fetchNextPage()}
      />

      <div className="sticky bottom-0 z-10 -mx-1 bg-background/95 px-1 pt-1 pb-0 backdrop-blur">
        {!composerReady ? (
          <Skeleton className="h-36 w-full" />
        ) : (
        <Composer
          // spec 057: remonta quando uma conversa é aberta daqui, para ela virar o canal
          // mesmo que outro tenha sido escolhido (o rascunho fica em drafts)
          key={`ticket-${ticket.ticketNumber}-${startedChatId ?? ''}`}
          channels={channels}
          noteTarget={noteTarget}
          ticketNumber={ticket.ticketNumber}
          requestorEmail={ticket.requestorEmail}
          commentBlockedReason={
            mirrored ? 'Ticket espelhado do TiFlux: o comentário é pelo TiFlux; a conversa do ERP segue respondível.' : null
          }
          defaultChannel={defaultChannel}
          startConversation={
            ready && can('chat.access') ? (
              <StartWhatsappAction
                ticket={ticket}
                compact
                onOpened={(chatId) => {
                  setStartedChatId(chatId);
                  sentFromComposer();
                }}
              />
            ) : null
          }
          drafts={drafts}
          autoFocus={search.responder === 1}
          onSent={sentFromComposer}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Registrar ação
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onSelect={() => setSheet('tempo')}>
                  <Timer />
                  Tempo (apontamento)
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setSheet('compromisso')}>
                  <CalendarPlus />
                  Compromisso na agenda
                </DropdownMenuItem>
                {/* spec 039: o agendamento pela placa, com este ticket já escolhido */}
                <DropdownMenuItem onSelect={() => setSheet('agendar')}>
                  <CalendarPlus />
                  Agendar {terms.resourceId}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setSheet('os')}>
                  <Wrench />
                  Ordem de serviço
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setSheet('checklist')}>
                  <ClipboardCheck />
                  Checklist
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
        )}
      </div>
      <div ref={endRef} aria-hidden />

      {/* ── Detalhes: painel sobreposto, fechado por padrão (FR-005) ─────── */}
      <Sheet
        open={search.detalhes === 1}
        onClose={() => setDetails(false)}
        title={`Detalhes do #${ticket.ticketNumber}`}
        description={`${ORIGIN_LABELS[ticket.createdVia]} · aberto em ${new Date(ticket.createdAt).toLocaleString('pt-BR')}`}
      >
        <div className="space-y-4">
          <RequestorCard ticket={ticket} onChangeClient={() => setChangeClient(true)} />
          <TicketInfoCard ticket={ticket} />
          <SlaCard ticket={ticket} />
          <TicketEntitiesCard ticketNumber={ticket.ticketNumber} readOnly={mirrored} />
          <section className="rounded-lg border bg-card p-4 shadow-sm">
            <h2 className="mb-2 text-base font-semibold">Valores</h2>
            <ValoresTab ticket={ticket} />
          </section>
          <AttachmentsSection ticketNumber={ticket.ticketNumber} readOnly={readOnly} />
          <TicketRatingCard ticketNumber={ticket.ticketNumber} />
          <section className="rounded-lg border bg-card p-4 text-sm shadow-sm">
            <h2 className="mb-2 text-base font-semibold">Origem e seguidores</h2>
            <p>
              <span className="text-muted-foreground">Origem: </span>
              {ORIGIN_LABELS[ticket.createdVia]}
            </p>
            <p>
              <span className="text-muted-foreground">Seguidores: </span>
              {ticket.followers?.trim() || '—'}
            </p>
          </section>
        </div>
      </Sheet>

      {/* ── Registrar ação: os mesmos componentes do Completo ─────────────── */}
      <Sheet open={sheet === 'tempo'} onClose={() => setSheet(null)} title="Tempo (apontamentos)" className="max-w-[min(56rem,100vw)]">
        <ApontamentosTab ticket={ticket} />
      </Sheet>
      <Sheet open={sheet === 'os'} onClose={() => setSheet(null)} title="Ordens de serviço" className="max-w-[min(48rem,100vw)]">
        <ServiceOrdersTab ticket={ticket} />
      </Sheet>
      <Sheet open={sheet === 'checklist'} onClose={() => setSheet(null)} title="Checklists" className="max-w-[min(48rem,100vw)]">
        <ChecklistsTab ticket={ticket} />
      </Sheet>
      {sheet === 'compromisso' ? (
        <CommitmentDrawer
          defaults={{ client: { id: ticket.client.id, name: ticket.client.name }, ticketNumber: ticket.ticketNumber }}
          onClose={() => setSheet(null)}
        />
      ) : null}
      {sheet === 'agendar' ? (
        <PlateCommitmentDrawer
          ticket={{
            ticketNumber: ticket.ticketNumber,
            title: ticket.title,
            plate: ticket.resourceLabel,
            clientId: ticket.client.id,
            clientName: ticket.client.name,
            deskName: ticket.desk.name,
            stageName: ticket.stage.name,
          }}
          onClose={() => setSheet(null)}
        />
      ) : null}

      {changeClient ? (
        <ChangeClientDialog
          ticketNumber={ticket.ticketNumber}
          currentClientName={ticket.client.name}
          onClose={() => setChangeClient(false)}
        />
      ) : null}
      {aiOpen ? <AiSummaryDialog ticketNumber={ticket.ticketNumber} onClose={() => setAiOpen(false)} /> : null}
      {dispatchMenu.dialog}

      {closeDialog ? (
        <CloseTicketDialog
          ticketNumber={ticket.ticketNumber}
          onClose={() => setCloseDialog(false)}
          onClosed={() => void onSent()}
        />
      ) : null}

      {concluding ? (
        <Dialog open onClose={() => setConcluding(null)} title={`Concluir o #${ticket.ticketNumber}?`}>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {concluding.draft ? <li>Há texto escrito no compositor que ainda não foi enviado.</li> : null}
            {concluding.missing.length > 0 ? (
              <li>
                Campos obrigatórios sem valor: <span className="text-foreground">{concluding.missing.join(', ')}</span>.
              </li>
            ) : null}
          </ul>
          {actionError ? <p className="mt-2 text-sm text-destructive">{actionError}</p> : null}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConcluding(null)}>
              Voltar
            </Button>
            {concluding.missing.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setConcluding(null);
                  setDetails(true);
                }}
              >
                Preencher os campos
              </Button>
            ) : null}
            <Button size="sm" disabled={action.isPending} onClick={() => void proceedToClose()}>
              Concluir mesmo assim
            </Button>
          </div>
        </Dialog>
      ) : null}

      {confirm ? (
        <Dialog
          open
          onClose={() => setConfirm(null)}
          title={confirm === 'cancel' ? `Cancelar o #${ticket.ticketNumber}` : `Reabrir o #${ticket.ticketNumber}`}
        >
          <p className="text-sm text-muted-foreground">
            {confirm === 'cancel'
              ? 'Tem certeza? Dá para desfazer depois com "Reabrir".'
              : 'Ele volta para o status aberto da mesa.'}
          </p>
          {action.isError ? <p className="mt-2 text-sm text-destructive">{errorMessage(action.error)}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
              Voltar
            </Button>
            <Button
              size="sm"
              variant={confirm === 'cancel' ? 'destructive' : 'default'}
              disabled={action.isPending}
              onClick={() =>
                action.mutate(confirm, {
                  onSuccess: () => {
                    setConfirm(null);
                    void onSent();
                  },
                })
              }
            >
              {confirm === 'cancel' ? `Cancelar ${terms.ticket}` : 'Reabrir'}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

/**
 * Técnico responsável em campo no cabeçalho (pedido da PoolTrack, 02/10) — o
 * mesmo seletor e o mesmo PATCH de "Informações do ticket". Editável também em
 * ticket fechado (correção de registro); no espelhado é só texto.
 */
function TechnicianControl({ ticket }: { ticket: TicketDetail }) {
  const patch = usePatchTicket(ticket.ticketNumber);
  if (isTicketMirrored(ticket)) {
    return <span className="text-muted-foreground">Técnico: {ticket.technician?.name ?? '—'}</span>;
  }
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      Técnico
      <TechnicianPicker
        compact
        value={ticket.technician}
        disabled={patch.isPending}
        onChange={(technicianId) => patch.mutate({ technicianId })}
      />
      {patch.isError ? <span className="text-destructive">{errorMessage(patch.error)}</span> : null}
    </span>
  );
}

/**
 * Responsável editável no cabeçalho. Escolher A SI MESMO é "Assumir" — com a
 * pergunta da pausa (015); escolher outra pessoa é o PATCH de sempre. A lista
 * é a equipe deste tenant, com a UX do técnico (pedido da PoolTrack, 05/10).
 */
function ResponsibleControl({ ticket, readOnly }: { ticket: TicketDetail; readOnly: boolean }) {
  const me = useAuthUser();
  const patch = usePatchTicket(ticket.ticketNumber);
  const { assume, pending, error, dialog } = useAssumeWork();

  if (readOnly) {
    return <span className="text-muted-foreground">Responsável: {ticket.responsible?.name ?? '—'}</span>;
  }
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      Responsável
      <TeamMemberPicker
        compact
        value={ticket.responsible}
        meId={me?.id}
        disabled={patch.isPending || pending}
        onChange={(id) => {
          if (id === null) return patch.mutate({ responsibleId: null });
          if (me && id === me.id) return assume({ kind: 'ticket', ticketNumber: ticket.ticketNumber });
          patch.mutate({ responsibleId: id });
        }}
      />
      {patch.isError || error ? (
        <span className="text-destructive">{error ?? errorMessage(patch.error)}</span>
      ) : null}
      {dialog}
    </span>
  );
}
